import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { AssetCameraKey, type SiteId } from "@visioni/a3s-schema";
import type { TenantScenarioStatusItem } from "@visioni/a3s-traffic-flow/fleet-messages";
import { getCameraDisplayName, getCameraKeyString, getSiteName, formatCameraLabel } from "@src/utils/key-parser";
import { toSentenceCase } from "@visioni/a3s-ui/utils";

interface ScenarioConfig {
  coordinate?: { lat: number; lng: number };
  friendlyName?: string;
}

export interface ScenarioRow {
  key: string;
  scenarioUuid: string;
  /** Display name — friendly name override if set, else the backend `scenarioName`. */
  scenarioName: string;
  /** Original backend name, kept for reference (e.g. "rename" affordance default). */
  backendScenarioName: string;
  siteId: SiteId;
  assetCameraKey: AssetCameraKey;
  cameraKey: string;
  cameraDisplayName: string;
  cameraSubtitle: string;
  updatedAt: string;
}

export interface ScenarioSiteGroup {
  siteId: string;
  siteName: string;
  siteSubtitle?: string;
  rows: ScenarioRow[];
}

export function groupScenariosBySite(
  items: TenantScenarioStatusItem[],
  cameras: CameraApiResponse[],
  configByUuid: Record<string, ScenarioConfig> = {},
  siteNames?: ReadonlyMap<string, string>,
): ScenarioSiteGroup[] {
  if (items.length === 0) return [];

  const cameraIndex = new Map<string, CameraApiResponse>();
  for (const camera of cameras) {
    cameraIndex.set(`${camera.key.asset}@${camera.key.id}`, camera);
  }

  const groups = new Map<string, ScenarioSiteGroup>();
  const orderedGroups: ScenarioSiteGroup[] = [];

  for (const item of items) {
    const cameraKey = `${item.asset}@${item.camera}`;
    const camera = cameraIndex.get(cameraKey);
    if (!camera) {
      console.warn(`[traffic-flow] orphan camera in scenario list: ${cameraKey}`);
      continue;
    }

    const siteIdStr = String(item.site);
    let group = groups.get(siteIdStr);
    if (!group) {
      const { name, subtitle } = getSiteName(camera, siteIdStr, siteNames);
      group = { siteId: siteIdStr, siteName: name, siteSubtitle: subtitle, rows: [] };
      groups.set(siteIdStr, group);
      orderedGroups.push(group);
    }

    const cameraKeyString = getCameraKeyString(camera);
    const cameraDisplayName = toSentenceCase(camera.name || getCameraDisplayName(cameraKeyString));
    const cameraSubtitle = formatCameraLabel(cameraKeyString);

    const assetCameraKey = AssetCameraKey.fromParts(item.asset, item.camera);
    for (const scenario of item.scenarios) {
      const friendly = configByUuid[scenario.scenarioUuid]?.friendlyName;
      group.rows.push({
        key: `${cameraKeyString}:${scenario.scenarioUuid}`,
        scenarioUuid: scenario.scenarioUuid,
        scenarioName: friendly && friendly.trim() !== "" ? friendly : scenario.scenarioName,
        backendScenarioName: scenario.scenarioName,
        siteId: item.site,
        assetCameraKey,
        cameraKey: cameraKeyString,
        cameraDisplayName,
        cameraSubtitle,
        updatedAt: item.updatedAt,
      });
    }
  }

  return orderedGroups;
}
